import {
  ActorProfileDetailFragmentFragment,
  useProfile
} from '@lopo-tech/lopo-api'
import React, { useCallback, useContext, useRef } from 'react'
import HeaderProfile from 'src/components/header/subheaders/pages/profile/header_profile'
import MetaTags from 'src/components/meta_tags/meta_tags'
import { FEED_DEFAULT_PER_PAGE } from 'src/constants/feed'
import {
  PROFILE_OG_TYPE,
  PROFILE_PRIVATE_TITLE,
  PROFILE_TITLE
} from 'src/constants/meta_tags'
import ProfileFeed from 'src/containers/smart_components/feeds/profile_feed'
import StandardPageTitle from 'src/containers/smart_components/page_title/page_title'
import { URLContext } from 'src/containers/url_provider/url_provider'
import { useGetProfileIdWithTypeFromUrlParams } from 'src/hooks_shared/use_url_params'
import { textResources } from 'src/lang/de'
import LayoutNoMap from 'src/layout/layout_no_map'
import { isPrivateProfile } from 'src/utils/profile'
import { UrlUtils } from 'src/utils/url/url'

const getPageTitle = (
  profile: ActorProfileDetailFragmentFragment | null | undefined
): string | undefined => {
  if (!profile) {
    return undefined
  }
  if (isPrivateProfile(profile.__typename)) {
    return PROFILE_PRIVATE_TITLE
  }
  if (profile.name) {
    return PROFILE_TITLE(profile.name)
  }
  return undefined
}

const ProfileFeedPage: React.FC = () => {
  const { slug, profileType } = useGetProfileIdWithTypeFromUrlParams()

  const { data, fetchMore, loading } = useProfile({
    variables: {
      slug,
      type: profileType,
      page: 1,
      perPage: FEED_DEFAULT_PER_PAGE
    }
  })
  const profile = data?.profile

  // we need to ensure that the callback always has the latest loading state
  // to avoid multiple fetchMore calls being triggered due to the getNextPage
  // being in a throttled fashion
  const loadingRef = useRef(loading)
  loadingRef.current = loading
  const getNextPage = useCallback(() => {
    if (profile?.contentItems.pageInfo.nextPage && !loadingRef.current) {
      fetchMore({
        variables: { page: profile.contentItems.pageInfo.nextPage }
      })
    }
  }, [fetchMore, profile?.contentItems.pageInfo.nextPage])

  const title = getPageTitle(profile)

  const locationShape = profile?.address.locationShape

  const { getNewsfeedRoute } = useContext(URLContext)

  // usePageTracking({ pageTitle: title, user })
  // useBlockProfileDetails(locationShape, profile?.type)

  return (
    <>
      {
        // we currently do not show any meta tag information for PrivateProfiles
        isPrivateProfile(profile?.__typename)
          ? (
            <MetaTags title={title} />
          )
          : (
            <MetaTags
              author={profile?.name}
              canonical={UrlUtils.currentUrlWithHostAndPath()}
              description={profile?.description}
              geo_placename={profile?.address.locationShape?.name}
              geo_position={
                locationShape &&
              `${locationShape.center.latitude};${locationShape.center.longitude}`
              }
              og_image={profile?.avatar.previewUrl}
              og_url={UrlUtils.currentUrlWithHostAndPath()}
              og_longitude={locationShape?.center.longitude.toString()}
              og_latitude={locationShape?.center.latitude.toString()}
              og_type={PROFILE_OG_TYPE}
              profile_username={profile?.name}
              title={title}
            />
          )
      }
      <LayoutNoMap
        pageTitle={
          <StandardPageTitle
            text={textResources.pageTitles.profile}
            customTag="p"
          />
        }
        subHeader={<HeaderProfile profile={profile} />}
        withHeaderMargin
      >
        <ProfileFeed
          getNewsfeedRoute={getNewsfeedRoute}
          getNextPage={getNextPage}
          isLoading={loading}
          profile={profile}
        />
      </LayoutNoMap>
    </>
  )
}

export default ProfileFeedPage
